import React, { useState, useRef, useEffect } from 'react';
import {
    FiCalendar,
    FiClock,
    FiMapPin,
    FiInfo,
    FiX,
    FiUser,
    FiMail,
    FiPhone,
    FiUsers,
    FiMessageSquare,
    FiCheckCircle,
    FiLock,
    FiAlertCircle,
    FiChevronRight,
    FiPlus,
    FiMinus,
    FiStar,
    FiLoader,
} from 'react-icons/fi';
import './PackageDetailsPage.css';
import api from "../../../Api";
import { useNavigate } from 'react-router-dom';

export default function BookingSection({ packageData, destinationName, mealPlans, specialRequests }) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [step, setStep] = useState(1);

    const [selectedMealPlan, setSelectedMealPlan] = useState(null);
    const [selectedSpecialRequests, setSelectedSpecialRequests] = useState([]);
    const [selectedVegItems, setSelectedVegItems] = useState([]);
    const [selectedNonVegItems, setSelectedNonVegItems] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [bookingResult, setBookingResult] = useState(null);
    const [error, setError] = useState(null);
    const modalRef = useRef(null);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        whatsapp: '',
        guests: selectedMealPlan?.min_persons,
        date: '',
        notes: '',
    });

    // Payment states
    const [paymentStep, setPaymentStep] = useState('idle'); // 'idle' | 'creating' | 'processing' | 'completed' | 'failed'
    const [paymentError, setPaymentError] = useState(null);

    // Restore booking state after login redirect
    useEffect(() => {
        const saved = sessionStorage.getItem('bookingRedirect');
        if (saved) {
            try {
                const savedState = JSON.parse(saved);
                if (savedState.shouldOpenModal && savedState.packageId === packageData?.id) {
                    if (savedState.formData) setFormData(savedState.formData);
                    if (savedState.selectedMealPlan) setSelectedMealPlan(savedState.selectedMealPlan);
                    if (savedState.selectedSpecialRequests) setSelectedSpecialRequests(savedState.selectedSpecialRequests);
                    if (savedState.selectedVegItems) setSelectedVegItems(savedState.selectedVegItems);
                    if (savedState.selectedNonVegItems) setSelectedNonVegItems(savedState.selectedNonVegItems);
                    setIsModalOpen(true);
                }
                sessionStorage.removeItem('bookingRedirect');
            } catch (e) {
                console.error('Failed to restore booking state', e);
                sessionStorage.removeItem('bookingRedirect');
            }
        }
    }, [packageData]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isModalOpen]);

    // Close modal on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (modalRef.current && !modalRef.current.contains(e.target)) {
                setIsModalOpen(false);
            }
        };
        if (isModalOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => { document.removeEventListener('mousedown', handleClickOutside); };
    }, [isModalOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleMealPlanSelect = (planId) => {
        const plan = mealPlans.find(p => p.id === planId);
        setSelectedMealPlan(plan);
        setSelectedVegItems([]);
        setSelectedNonVegItems([]);
    };

    const toggleVegItem = (itemId) => {
        if (!selectedMealPlan) return;
        const maxVeg = selectedMealPlan.max_veg_items || 0;
        const isSelected = selectedVegItems.includes(itemId);
        if (isSelected) {
            setSelectedVegItems(prev => prev.filter(id => id !== itemId));
        } else {
            if (selectedVegItems.length >= maxVeg) {
                setError(`You can select at most ${maxVeg} veg items.`);
                return;
            }
            setSelectedVegItems(prev => [...prev, itemId]);
            setError(null);
        }
    };

    const toggleNonVegItem = (itemId) => {
        if (!selectedMealPlan) return;
        const maxNonVeg = selectedMealPlan.max_nonveg_items || 0;
        const isSelected = selectedNonVegItems.includes(itemId);
        if (isSelected) {
            setSelectedNonVegItems(prev => prev.filter(id => id !== itemId));
        } else {
            if (selectedNonVegItems.length >= maxNonVeg) {
                setError(`You can select at most ${maxNonVeg} non‑veg items.`);
                return;
            }
            setSelectedNonVegItems(prev => [...prev, itemId]);
            setError(null);
        }
    };

    const handleSpecialRequestToggle = (requestId) => {
        setSelectedSpecialRequests(prev => {
            const exists = prev.find(r => r.id === requestId);
            if (exists) {
                return prev.filter(r => r.id !== requestId);
            } else {
                return [...prev, { id: requestId, quantity: 1 }];
            }
        });
    };

    const handleSpecialRequestQuantity = (requestId, change) => {
        setSelectedSpecialRequests(prev =>
            prev.map(r => {
                if (r.id === requestId) {
                    const newQty = Math.max(1, (r.quantity || 1) + change);
                    return { ...r, quantity: newQty };
                }
                return r;
            })
        );
    };

    const handleNext = () => {
        if (step === 1) {
            if (!formData.name || !formData.email || !formData.phone) {
                setError('Please fill in all required contact fields.');
                return;
            }
            setError(null);
        }
        if (step === 2) {
            if (!formData.date) {
                setError('Please select an event date.');
                return;
            }
            if (!selectedMealPlan) {
                setError('Please select a meal plan.');
                return;
            }
            setError(null);
        }
        if (step < 3) setStep(step + 1);
    };

    const handlePrev = () => {
        if (step > 1) setStep(step - 1);
        setError(null);
    };

    // ---- Book Now with auth check ----
    const handleBookNow = () => {
        const token = localStorage.getItem('accessToken');
        if (!token) {
            const bookingState = {
                packageId: packageData?.id,
                destinationName,
                shouldOpenModal: true,
                formData,
                selectedMealPlan,
                selectedSpecialRequests,
                selectedVegItems,
                selectedNonVegItems,
            };
            sessionStorage.setItem('bookingRedirect', JSON.stringify(bookingState));
            const currentPath = window.location.pathname + window.location.search;
            window.location.href = `/signup?redirect=${encodeURIComponent(currentPath)}`;
            return;
        }
        setIsModalOpen(true);
    };

    // ---- Create Razorpay Order ----
    const createRazorpayOrder = async (bookingId) => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await api.post(
                `/wedding/bookings/${bookingId}/razorpay-order`,
                { amount: bookingResult.grand_total || 0, payment_type: "full" },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const result = response.data;
            if (result.status) {
                return result.data; // returns order details
            } else {
                throw new Error(result.message || 'Failed to create payment order.');
            }
        } catch (err) {
            throw new Error(err.message || 'Payment order creation failed.');
        }
    };

    // ---- Verify Payment ----
    const verifyPayment = async (bookingId, paymentId, orderId, signature) => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await api.post(
                `/wedding/bookings/${bookingId}/razorpay-verify`,
                {
                    razorpay_payment_id: paymentId,
                    razorpay_order_id: orderId,
                    razorpay_signature: signature,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const result = response.data;
            if (result.status) {
                return result.data;
            } else {
                throw new Error(result.message || 'Payment verification failed.');
            }
        } catch (err) {
            throw new Error(err.message || 'Payment verification failed.');
        }
    };

    // ---- Open Razorpay Checkout ----
    const openRazorpayCheckout = (orderData, bookingId) => {
        const options = {
            key: orderData.key_id,                 // ✅ from response
            amount: orderData.amount_paise,        // in paise
            currency: orderData.currency,
            name: packageData?.name || 'Wedding Package',
            description: `Booking #${bookingId}`,
            order_id: orderData.order_id,          // ✅ from response
            prefill: {
                name: orderData.prefill?.name || formData.name,
                email: orderData.prefill?.email || formData.email,
                contact: orderData.prefill?.contact || formData.phone,
            },
            theme: {
                color: '#F37254',
            },
            modal: {
                ondismiss: () => {
                    setPaymentStep('idle');
                    setError('Payment was cancelled.');
                },
            },
            handler: async (response) => {
                // Payment successful - verify
                setPaymentStep('processing');
                try {
                    const verifiedData = await verifyPayment(
                        bookingId,
                        response.razorpay_payment_id,
                        response.razorpay_order_id,
                        response.razorpay_signature
                    );
                    setBookingResult(prev => ({
                        ...prev,
                        payment_status: 'paid',
                        verified_data: verifiedData,
                    }));
                    setPaymentStep('completed');
                    setTimeout(() => {
                        setIsModalOpen(false);
                        resetForm();
                    }, 5000);
                    navigate("/profile/destination-wedding")
                } catch (err) {
                    setPaymentStep('failed');
                    setError(err.message);
                }
            },
        };

        const razorpay = new window.Razorpay(options);
        razorpay.open();
        setPaymentStep('processing'); // will override 'creating'
    };

    // ---- Submit booking (NO automatic payment) ----
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);
        setPaymentStep('idle');
        setPaymentError(null);

        const payload = {
            package_id: packageData.id,
            head_count: parseInt(formData.guests),
            event_date: formData.date,
            contact_name: formData.name,
            contact_email: formData.email,
            contact_mobile: formData.phone,
            contact_whatsapp: formData.whatsapp || formData.phone,
            notes: formData.notes || '',
            meal_plan_id: selectedMealPlan?.id || null,
            menu_selections: {
                veg: selectedVegItems,
                nonveg: selectedNonVegItems,
            },
            special_requests: selectedSpecialRequests.map(r => ({
                id: r.id,
                quantity: r.quantity || 1
            })),
            activities: []
        };

        try {
            const token = localStorage.getItem('accessToken');
            const bookingResponse = await api.post('/wedding/bookings', payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const bookingResultData = bookingResponse.data;
            if (!bookingResultData.status) {
                throw new Error(bookingResultData.message || 'Booking creation failed.');
            }

            const bookingInfo = bookingResultData.data;
            setBookingResult(bookingInfo);
            // Do NOT create order here – user will click "Pay Now"
        } catch (err) {
            if (err.response && err.response.status === 401) {
                const bookingState = {
                    packageId: packageData?.id,
                    destinationName,
                    shouldOpenModal: true,
                    formData,
                    selectedMealPlan,
                    selectedSpecialRequests,
                    selectedVegItems,
                    selectedNonVegItems,
                };
                sessionStorage.setItem('bookingRedirect', JSON.stringify(bookingState));
                const currentPath = window.location.pathname + window.location.search;
                window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
            } else {
                console.error('Booking submission error:', err);
                setError(err.response?.data?.message || 'An error occurred. Please try again.');
                setPaymentStep('failed');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // ---- Pay Now handler (trigger order creation) ----
    const handlePayNow = async () => {
        if (!bookingResult || !bookingResult.booking_id) {
            setError('Booking information missing.');
            return;
        }
        setPaymentStep('creating');
        setError(null);
        try {
            const orderData = await createRazorpayOrder(bookingResult.booking_id);
            openRazorpayCheckout(orderData, bookingResult.booking_id);
        } catch (err) {
            setError(err.message);
            setPaymentStep('failed');
        }
    };

    const resetForm = () => {
        setStep(1);
        setFormData({ name: '', email: '', phone: '', whatsapp: '', guests: "", date: '', notes: '' });
        setSelectedMealPlan(null);
        setSelectedSpecialRequests([]);
        setSelectedVegItems([]);
        setSelectedNonVegItems([]);
        setIsSubmitting(false);
        setBookingResult(null);
        setError(null);
        setPaymentStep('idle');
        setPaymentError(null);
        sessionStorage.removeItem('bookingRedirect');
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0,
        }).format(price);
    };

    const hasVegMenu = selectedMealPlan?.has_veg_menu && selectedMealPlan?.veg_menu?.length > 0;
    const hasNonVegMenu = selectedMealPlan?.has_nonveg_menu && selectedMealPlan?.nonveg_menu?.length > 0;

    // ---- Render helper for payment status ----
    const renderPaymentStatus = () => {
        if (paymentStep === 'creating') {
            return (
                <div className="pkgd-payment-loading">
                    <FiLoader className="pkgd-spinner" />
                    <p>Preparing payment...</p>
                </div>
            );
        }
        if (paymentStep === 'processing') {
            return (
                <div className="pkgd-payment-loading">
                    <FiLoader className="pkgd-spinner" />
                    <p>Processing your payment...</p>
                </div>
            );
        }
        if (paymentStep === 'completed') {
            return (
                <div className="pkgd-payment-success">
                    <FiCheckCircle className="pkgd-success-icon" />
                    <h3>Payment Successful!</h3>
                    <p>Your booking is now confirmed.</p>
                    {bookingResult?.verified_data && (
                        <p className="pkgd-booking-number">
                            Payment ID: {bookingResult.verified_data.razorpay_payment_id}
                        </p>
                    )}
                </div>
            );
        }
        if (paymentStep === 'failed') {
            return (
                <div className="pkgd-payment-failed">
                    <FiAlertCircle className="pkgd-error-icon" />
                    <h3>Booking Failed</h3>
                    <p>{error || 'Something went wrong. Please try again.'}</p>
                    <button
                        className="pkgd-btn-retry"
                        onClick={() => {
                            setPaymentStep('idle');
                            setError(null);
                            if (bookingResult?.booking_id) {
                                setPaymentStep('creating');
                                createRazorpayOrder(bookingResult.booking_id)
                                    .then(orderData => openRazorpayCheckout(orderData, bookingResult.booking_id))
                                    .catch(err => {
                                        setError(err.message);
                                        setPaymentStep('failed');
                                    });
                            }
                        }}
                    >
                        Retry Payment
                    </button>
                </div>
            );
        }
        return null;
    };

    return (
        <>
            {/* Sidebar */}
            <div className="pkgd-booking-sidebar">
                <div className="pkgd-booking-card">
                    <h3>Book This Package</h3>
                    <div className="pkgd-booking-quick-info">
                        <div className="pkgd-booking-info-item">
                            <FiCalendar />
                            <span>{packageData?.duration_days} Days</span>
                        </div>
                        <div className="pkgd-booking-info-item">
                            <FiClock />
                            <span>{packageData?.duration_nights} Nights</span>
                        </div>
                        <div className="pkgd-booking-info-item">
                            <FiMapPin />
                            <span>{destinationName}</span>
                        </div>
                    </div>

                    <button className="pkgd-btn-book" onClick={handleBookNow}>
                        Book Now <FiChevronRight />
                    </button>

                    <p className="pkgd-booking-note">
                        <FiInfo />
                        Free cancellation within 24 hours
                    </p>
                </div>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="pkgd-modal-overlay">
                    <div className="pkgd-modal-container" ref={modalRef}>
                        <button
                            className="pkgd-modal-close"
                            onClick={() => {
                                setIsModalOpen(false);
                                resetForm();
                            }}
                        >
                            <FiX />
                        </button>

                        <div className="pkgd-modal-header">
                            <h2>Book Your Dream Wedding</h2>
                            <p>
                                <strong>{packageData?.name}</strong> at {destinationName}
                            </p>
                        </div>

                        {/* Show payment progress if after booking */}
                        {paymentStep !== 'idle' && (
                            <div className="pkgd-payment-overlay">
                                {renderPaymentStatus()}
                            </div>
                        )}

                        {/* Booking form OR summary */}
                        {!bookingResult ? (
                            // -------- FORM (steps 1-3) --------
                            <>
                                <div className="pkgd-modal-steps">
                                    <div className={`pkgd-step-indicator ${step >= 1 ? 'pkgd-active' : ''}`}>
                                        <span>1</span>
                                        <label>Contact</label>
                                    </div>
                                    <div className={`pkgd-step-indicator ${step >= 2 ? 'pkgd-active' : ''}`}>
                                        <span>2</span>
                                        <label>Customize</label>
                                    </div>
                                    <div className={`pkgd-step-indicator ${step >= 3 ? 'pkgd-active' : ''}`}>
                                        <span>3</span>
                                        <label>Confirm</label>
                                    </div>
                                </div>

                                {error && (
                                    <div className="pkgd-error-message">
                                        <FiAlertCircle />
                                        <span>{error}</span>
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} className="pkgd-modal-form">
                                    {step === 1 && (
                                        <div className="pkgd-form-step">
                                            <div className="pkgd-form-group">
                                                <label><FiUser /> Full Name <span>*</span></label>
                                                <input
                                                    type="text"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    placeholder="Your full name"
                                                    required
                                                />
                                            </div>
                                            <div className="pkgd-form-group">
                                                <label><FiMail /> Email <span>*</span></label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    placeholder="you@example.com"
                                                    required
                                                />
                                            </div>
                                            <div className="pkgd-form-group">
                                                <label><FiPhone /> Phone <span>*</span></label>
                                                <input
                                                    type="tel"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    placeholder="+91 98765 43210"
                                                    required
                                                />
                                            </div>
                                            <div className="pkgd-form-group">
                                                <label><FiMessageSquare /> WhatsApp (optional)</label>
                                                <input
                                                    type="tel"
                                                    name="whatsapp"
                                                    value={formData.whatsapp}
                                                    onChange={handleChange}
                                                    placeholder="+91 98765 43210"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {step === 2 && (
                                        <div className="pkgd-form-step">
                                            <div className="pkgd-form-group">
                                                <label><FiCalendar /> Event Date <span>*</span></label>
                                                <input
                                                    type="date"
                                                    name="date"
                                                    value={formData.date}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>


                                            <div className="pkgd-form-group">
                                                <label><FiStar /> Select Meal Plan <span>*</span></label>
                                                <select
                                                    value={selectedMealPlan?.id || ''}
                                                    onChange={(e) => handleMealPlanSelect(parseInt(e.target.value))}
                                                    required
                                                >
                                                    <option value="">Choose a meal plan</option>
                                                    {mealPlans.map((plan) => (
                                                        <option key={plan.id} value={plan.id}>
                                                            {plan.plan_name} - ₹{plan.price_per_person}/person
                                                        </option>
                                                    ))}
                                                </select>
                                                {selectedMealPlan && (
                                                    <div className="pkgd-meal-preview">
                                                        <p><strong>{selectedMealPlan.plan_name}</strong></p>
                                                        <p>{selectedMealPlan.description}</p>
                                                        <p>Min {selectedMealPlan.min_persons} - Max {selectedMealPlan.max_persons} guests</p>
                                                    </div>
                                                )}
                                            </div>

                                            {selectedMealPlan && (
                                                <div className="pkgd-form-group pkgd-menu-selection">
                                                    <label>Select Menu Items</label>
                                                    <div className="pkgd-menu-categories">
                                                        {hasVegMenu && (
                                                            <div className="pkgd-menu-category">
                                                                <h5>Veg Items (max {selectedMealPlan.max_veg_items})</h5>
                                                                <div className="pkgd-menu-items">
                                                                    {selectedMealPlan.veg_menu.map((item) => {
                                                                        const isSelected = selectedVegItems.includes(item.id);
                                                                        const isDisabled = !isSelected && selectedVegItems.length >= selectedMealPlan.max_veg_items;
                                                                        return (
                                                                            <label key={item.id} className="pkgd-menu-item">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={isSelected}
                                                                                    onChange={() => toggleVegItem(item.id)}
                                                                                    disabled={isDisabled}
                                                                                />
                                                                                <span><strong>{item.item_name}</strong> – {item.category_label}</span>
                                                                                {item.description && <small>({item.description})</small>}
                                                                            </label>
                                                                        );
                                                                    })}
                                                                </div>
                                                                <span className="pkgd-menu-count">
                                                                    Selected: {selectedVegItems.length} / {selectedMealPlan.max_veg_items}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {hasNonVegMenu && (
                                                            <div className="pkgd-menu-category">
                                                                <h5>Non‑Veg Items (max {selectedMealPlan.max_nonveg_items})</h5>
                                                                <div className="pkgd-menu-items">
                                                                    {selectedMealPlan.nonveg_menu.map((item) => {
                                                                        const isSelected = selectedNonVegItems.includes(item.id);
                                                                        const isDisabled = !isSelected && selectedNonVegItems.length >= selectedMealPlan.max_nonveg_items;
                                                                        return (
                                                                            <label key={item.id} className="pkgd-menu-item">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={isSelected}
                                                                                    onChange={() => toggleNonVegItem(item.id)}
                                                                                    disabled={isDisabled}
                                                                                />
                                                                                <span><strong>{item.item_name}</strong> – {item.category_label}</span>
                                                                                {item.description && <small>({item.description})</small>}
                                                                            </label>
                                                                        );
                                                                    })}
                                                                </div>
                                                                <span className="pkgd-menu-count">
                                                                    Selected: {selectedNonVegItems.length} / {selectedMealPlan.max_nonveg_items}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {!hasVegMenu && !hasNonVegMenu && (
                                                            <p className="pkgd-menu-empty">No menu items available for this plan.</p>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {specialRequests && specialRequests.length > 0 && (
                                                <div className="pkgd-form-group">
                                                    <label>Special Requests</label>
                                                    <div className="pkgd-special-list">
                                                        {specialRequests.map((req) => {
                                                            const selected = selectedSpecialRequests.find(r => r.id === req.id);
                                                            return (
                                                                <div key={req.id} className="pkgd-special-item">
                                                                    <label className="pkgd-checkbox-label">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={!!selected}
                                                                            onChange={() => handleSpecialRequestToggle(req.id)}
                                                                        />
                                                                        <span>{req.title || `Request #${req.id}`}</span>
                                                                    </label>
                                                                    {selected && (
                                                                        <div className="pkgd-quantity-control">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleSpecialRequestQuantity(req.id, -1)}
                                                                            >
                                                                                <FiMinus />
                                                                            </button>
                                                                            <span>{selected.quantity || 1}</span>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleSpecialRequestQuantity(req.id, 1)}
                                                                            >
                                                                                <FiPlus />
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                            <div className="pkgd-form-group">
                                                <label><FiUsers /> Number of Guests <span>*</span></label>
                                                <input
                                                    type="number"
                                                    name="guests"
                                                    value={formData.guests}
                                                    onChange={handleChange}
                                                    placeholder="Enter number of guests"
                                                    min={selectedMealPlan?.min_persons}
                                                    required
                                                />
                                            </div>

                                            <div className="pkgd-form-group">
                                                <label><FiMessageSquare /> Additional Notes</label>
                                                <textarea
                                                    name="notes"
                                                    value={formData.notes}
                                                    onChange={handleChange}
                                                    placeholder="Any special requirements or preferences..."
                                                    rows="3"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {step === 3 && (
                                        <div className="pkgd-form-step pkgd-confirmation-step">
                                            <div className="pkgd-confirmation-box">
                                                <h4>Review Your Booking</h4>
                                                <div className="pkgd-confirmation-details">
                                                    <div className="pkgd-conf-item">
                                                        <span className="pkgd-conf-label">Package</span>
                                                        <span className="pkgd-conf-value">{packageData?.name}</span>
                                                    </div>
                                                    <div className="pkgd-conf-item">
                                                        <span className="pkgd-conf-label">Destination</span>
                                                        <span className="pkgd-conf-value">{destinationName}</span>
                                                    </div>
                                                    <div className="pkgd-conf-item">
                                                        <span className="pkgd-conf-label">Duration</span>
                                                        <span className="pkgd-conf-value">
                                                            {packageData?.duration_days} Days, {packageData?.duration_nights} Nights
                                                        </span>
                                                    </div>
                                                    <div className="pkgd-conf-item">
                                                        <span className="pkgd-conf-label">Name</span>
                                                        <span className="pkgd-conf-value">{formData.name}</span>
                                                    </div>
                                                    <div className="pkgd-conf-item">
                                                        <span className="pkgd-conf-label">Email</span>
                                                        <span className="pkgd-conf-value">{formData.email}</span>
                                                    </div>
                                                    <div className="pkgd-conf-item">
                                                        <span className="pkgd-conf-label">Phone</span>
                                                        <span className="pkgd-conf-value">{formData.phone}</span>
                                                    </div>
                                                    {formData.whatsapp && (
                                                        <div className="pkgd-conf-item">
                                                            <span className="pkgd-conf-label">WhatsApp</span>
                                                            <span className="pkgd-conf-value">{formData.whatsapp}</span>
                                                        </div>
                                                    )}
                                                    <div className="pkgd-conf-item">
                                                        <span className="pkgd-conf-label">Event Date</span>
                                                        <span className="pkgd-conf-value">
                                                            {new Date(formData.date).toLocaleDateString('en-IN', {
                                                                day: 'numeric',
                                                                month: 'long',
                                                                year: 'numeric',
                                                            })}
                                                        </span>
                                                    </div>
                                                    <div className="pkgd-conf-item">
                                                        <span className="pkgd-conf-label">Guests</span>
                                                        <span className="pkgd-conf-value">{formData.guests}</span>
                                                    </div>
                                                    {selectedMealPlan && (
                                                        <>
                                                            <div className="pkgd-conf-item">
                                                                <span className="pkgd-conf-label">Meal Plan</span>
                                                                <span className="pkgd-conf-value">{selectedMealPlan.plan_name}</span>
                                                            </div>
                                                            {selectedVegItems.length > 0 && (
                                                                <div className="pkgd-conf-item">
                                                                    <span className="pkgd-conf-label">Veg Items</span>
                                                                    <span className="pkgd-conf-value">
                                                                        {selectedVegItems.map(id => {
                                                                            const item = selectedMealPlan.veg_menu.find(i => i.id === id);
                                                                            return item ? item.item_name : id;
                                                                        }).join(', ')}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {selectedNonVegItems.length > 0 && (
                                                                <div className="pkgd-conf-item">
                                                                    <span className="pkgd-conf-label">Non‑Veg Items</span>
                                                                    <span className="pkgd-conf-value">
                                                                        {selectedNonVegItems.map(id => {
                                                                            const item = selectedMealPlan.nonveg_menu.find(i => i.id === id);
                                                                            return item ? item.item_name : id;
                                                                        }).join(', ')}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                    {selectedSpecialRequests.length > 0 && (
                                                        <div className="pkgd-conf-item">
                                                            <span className="pkgd-conf-label">Special Requests</span>
                                                            <span className="pkgd-conf-value">
                                                                {selectedSpecialRequests.map(r => `${r.id}×${r.quantity}`).join(', ')}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {formData.notes && (
                                                        <div className="pkgd-conf-item">
                                                            <span className="pkgd-conf-label">Notes</span>
                                                            <span className="pkgd-conf-value">{formData.notes}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="pkgd-confirmation-note">
                                                    <FiAlertCircle />
                                                    <span>By confirming, you agree to our terms and conditions.</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="pkgd-modal-actions">
                                        {step > 1 && (
                                            <button type="button" className="pkgd-btn-prev" onClick={handlePrev}>
                                                Back
                                            </button>
                                        )}
                                        {step < 3 ? (
                                            <button type="button" className="pkgd-btn-next" onClick={handleNext}>
                                                Next Step <FiChevronRight />
                                            </button>
                                        ) : (
                                            <button
                                                type="submit"
                                                className="pkgd-btn-submit"
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <span className="pkgd-spinner"></span>
                                                        Submitting...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FiLock />
                                                        Confirm Booking & Pay
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </form>
                            </>
                        ) : (
                            // -------- BOOKING SUMMARY (after creation) --------
                            <div className="pkgd-booking-summary-full">
                                <h3>Booking Created!</h3>
                                <p className="pkgd-booking-number">
                                    Booking #: {bookingResult.booking_code || bookingResult.booking_id}
                                </p>
                                <div className="pkgd-booking-summary">
                                    <div className="pkgd-summary-row">
                                        <span>Subtotal</span>
                                        <span>{formatPrice(bookingResult.subtotal)}</span>
                                    </div>
                                    <div className="pkgd-summary-row">
                                        <span>GST ({bookingResult.gst_percentage}%)</span>
                                        <span>{formatPrice(bookingResult.gst_amount)}</span>
                                    </div>
                                    <div className="pkgd-summary-row pkgd-total">
                                        <span>Grand Total</span>
                                        <span>{formatPrice(bookingResult.grand_total)}</span>
                                    </div>
                                </div>

                                {/* Payment status inline (optional) */}
                                {paymentStep === 'completed' && (
                                    <div className="pkgd-payment-success">
                                        <FiCheckCircle className="pkgd-success-icon" />
                                        <p>Payment successful! Your booking is confirmed.</p>
                                    </div>
                                )}
                                {paymentStep === 'failed' && (
                                    <div className="pkgd-payment-failed">
                                        <FiAlertCircle className="pkgd-error-icon" />
                                        <p>{error || 'Payment failed.'}</p>
                                        <button
                                            className="pkgd-btn-retry"
                                            onClick={() => {
                                                setPaymentStep('creating');
                                                setError(null);
                                                createRazorpayOrder(bookingResult.booking_id)
                                                    .then(orderData => openRazorpayCheckout(orderData, bookingResult.booking_id))
                                                    .catch(err => {
                                                        setError(err.message);
                                                        setPaymentStep('failed');
                                                    });
                                            }}
                                        >
                                            Retry Payment
                                        </button>
                                    </div>
                                )}

                                {/* Pay Now button – visible when no payment in progress */}
                                {paymentStep === 'idle' && (
                                    <button
                                        className="pkgd-btn-pay"
                                        onClick={handlePayNow}
                                    >
                                        Pay Now
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}